import React from 'react';
import { makeStyles } from '@mui/styles';
import Carousel from 'nuka-carousel';
import { RootState } from '@Common/models/redux/store';
import { useSelector } from 'react-redux';
import { Theme } from '@quillbot/design-system';

const howData = [
  {
    title: 'Understand the Material',
    text: 'Read and review the source material a few times until you have a strong understanding of what it’s trying to convey.',
    color: '#00488A',
  },
  {
    title: 'Test Your Knowledge',
    text: 'Without looking at the text, write down what you remember about the general message. Be careful not to write down any parts of it that you memorized to avoid plagiarizing when you’re trying to paraphrase.',
    color: '#CD323C',
  },
  {
    title: 'Play with Words',
    text: 'Play with and modify word usage, sentence structure, style, and overall flow. Make it as unique as possible while staying true to the original source intent.',
    color: '#E36B00',
  },
  {
    title: 'Replace Mirrored Content',
    text: 'Compare your paraphrase with the source. Change any words or phrases that you remembered and wrote down that are too close to the original text and could be interpreted as plagiarism.',
    color: '#7017FF',
  },
  {
    title: 'Make It Original',
    text: 'Consult the text to add in important details that previously you left out. Link your ideas to the paraphrased information to support your arguments in a unique way.',
    color: '#4C9459',
  },
];

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    backgroundColor: '#EBE1FF',
  },
  container: {
    padding: '60px 0',
    maxWidth: 665,
    margin: '0 auto',
    [theme.breakpoints.down('sm')]: {
      padding: '36px 32px',
      overflow: 'hidden',
    },
  },
  title: {
    fontSize: 24,
    fontWeight: 600,
    margin: '0 0 20px',
    lineHeight: '16px',
  },
  text: {
    fontSize: 16,
  },
  howCardsList: {
    marginTop: 40,
    [theme.breakpoints.down('sm')]: {
      margin: '40px -32px',
    },
  },
  mobileSlider: {
    '& .slider-slide': {
      padding: '16px',
    },
    '& .slider-control-bottomcenter': {
      bottom: '-10px !important',
      '& .paging-item button svg': {
        display: 'none',
      },
      '& .paging-item': {
        marginLeft: 6,
      },
      '& .paging-item button': {
        width: '8px',
        height: '8px',
        borderRadius: '100px',
        opacity: '1 !important',
        backgroundColor: `${theme.specialColors.CIRCULAR_PROGRESS}  !important`,
        padding: 0,
      },
      '& .paging-item.active button': {
        backgroundColor: `${theme.palette.primary.main} !important`,
        width: '18px',
        borderRadius: '50px',
      },
    },
  },
  howCard: {
    display: 'flex',
    borderRadius: '24px',
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[3],
    overflow: 'hidden',
    marginBottom: 32,
    '&:last-child': {
      marginBottom: 0,
    },
    [theme.breakpoints.down('sm')]: {
      position: 'relative',
      borderRadius: '20px',
      // margin: '16px',
      minHeight: '290px',
      '&:last-child': {
        // marginBottom: '16px',
      },
    },
  },
  numberWrapper: {
    padding: '22px 0 22px 25px',
    color: theme.palette.secondary.contrastText,
    position: 'relative',
    [theme.breakpoints.down('sm')]: {
      position: 'absolute',
      left: 0,
      padding: '16px 0px 22px 18px',
    },
  },
  arrow: {
    position: 'absolute',
    top: 0,
    right: -70,
    borderLeft: '70px solid black',
    borderTop: '82px solid transparent',
    borderBottom: '82px solid transparent',
    [theme.breakpoints.down('sm')]: {
      right: -30,
      borderLeft: '30px solid black',
      borderTop: '53px solid transparent',
      borderBottom: '53px solid transparent',
    },
  },
  numberLabel: {
    textTransform: 'uppercase',
    fontSize: 24,
    lineHeight: '16px',
    fontWeight: 700,
    [theme.breakpoints.down('sm')]: {
      fontSize: 12,
    },
  },
  number: {
    fontSize: 96,
    lineHeight: 1,
    fontWeight: 300,
    marginLeft: -8,
    marginTop: 8,
    [theme.breakpoints.down('sm')]: {
      fontSize: 48,
      marginTop: 4,
    },
  },
  cardContent: {
    padding: '28px 28px 28px 90px',
    [theme.breakpoints.down('sm')]: {
      paddingLeft: 108,
    },
  },
  cardTitle: {
    fontSize: 24,
    lineHeight: 1,
    fontWeight: 700,
    marginBottom: 16,
    [theme.breakpoints.down('sm')]: {
      fontSize: 20,
    },
  },
  cardText: {
    fontSize: 16,
  },
}));

function HowParaphrase() {
  const isMobile = useSelector((state: RootState) => state.common.platform.isMobile);
  const classes = useStyles();

  return (
    <div className={classes.root}>
      <div className={classes.container}>
        <h2 className={classes.title}>How to Paraphrase</h2>
        <div className={classes.text}>
          <p>
            Paraphrasing, like most things, is a process. When determining how to paraphrase, there
            are a few things you must do every time. The paraphrasing steps are easy but should be
            followed to a T in order to ensure you’re conveying the referenced information
            accurately.
          </p>
          <p>You can paraphrase correctly every time by following the five steps below:</p>
        </div>

        {isMobile ? (
          <div className={classes.howCardsList}>
            <Carousel
              className={classes.mobileSlider}
              slideWidth={1}
              cellSpacing={16}
              renderCenterLeftControls={null}
              renderCenterRightControls={null}
              frameOverflow="visible"
            >
              {howData.map((item, index) => (
                <HowCard
                  key={item.title}
                  title={item.title}
                  text={item.text}
                  number={index + 1}
                  color={item.color}
                />
              ))}
            </Carousel>
          </div>
        ) : (
          <div className={classes.howCardsList}>
            {howData.map((item, index) => (
              <HowCard
                key={item.title}
                title={item.title}
                text={item.text}
                number={index + 1}
                color={item.color}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default HowParaphrase;

function HowCard({ title, text, color, number }: any) {
  const classes = useStyles();
  return (
    <div className={classes.howCard}>
      <div className={classes.numberWrapper} style={{ backgroundColor: color }}>
        <div className={classes.arrow} style={{ borderLeftColor: color }} />
        <div className={classes.numberLabel}>Step</div>
        <div className={classes.number}>0{number}</div>
      </div>
      <div className={classes.cardContent}>
        <div className={classes.cardTitle}>{title}</div>
        <div className={classes.cardText}>{text}</div>
      </div>
    </div>
  );
}
